import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import {
	useGetUserByEmail,
	useGetUserCampaignInvitations,
	useSendGuestInvite,
	useUpdateUserCampaignInvitation,
	useUpdateUserContactInfo,
	useUpdateUserPassword
} from 'api/invite/hooks';
import { userLogin } from 'api/invite/fetchers';
import { emailRegex, passwordRegex } from 'utils';
import Styles from './accountCreationStep.module.scss';

export const AccountCreationStep = () => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [step, setStep] = useState(0);
	const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);
	const [hasSetPassword, setHasSetPassword] = useState(false);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmNewPassword, setConfirmNewPassword] = useState('');
	const [guestEmail, setGuestEmail] = useState('');
	const [error, setError] = useState({
		email: '',
		guestEmail: '',
		newPassword: ''
	});

	const restaurantID = searchParams.get('restaurantID');
	const campaignID = searchParams.get('campaignID');
	const campaignInviteID = searchParams.get('campaignInviteID');
	const userCurrentID = localStorage.getItem('invitee_user_id');

	const {
		isFetching: isCheckingEmail,
		refetch: fetchUserByEmail
	} = useGetUserByEmail({ email, options: { enabled: false } });

	const {
		refetch: fetchGuestByEmail
	} = useGetUserByEmail({ email: guestEmail, options: { enabled: false } });

	const {
		data: userCampaignInvitations,
	} = useGetUserCampaignInvitations({
		restaurantID,
		campaignID,
		userID: userCurrentID
	});

	const guestInviteInfo = (userCampaignInvitations
		&& userCampaignInvitations.campaign_invites
		&& userCampaignInvitations.campaign_invites.length > 0)
		&& userCampaignInvitations.campaign_invites[0];

	const {
		mutate: updateContactInfo,
		isPending: isUpdatingUserContactInfo
	} = useUpdateUserContactInfo({
		// eslint-disable-next-line no-unused-vars
		onSuccess: (_data, variables) => {
			if (variables.flow === 'user-info-update') {
				toast.success('Your email has been saved.');
				setStep(1);

				return;
			}

			if (variables.flow === 'guest-info-update') {
				sendGuestInvite({
					restaurantID,
					campaignID,
					campaignInviteID: guestInviteInfo.id,
					email: guestEmail
				});
			}
		},
		onError: (error) => {
			const defaultMessage = 'Something went wrong while updating info';
			const responseMessage = error?.response?.data?.message;
			const errorMessage = responseMessage && responseMessage.length <= 100
				? responseMessage
				: defaultMessage;

			toast.error(errorMessage);
		}
	});

	const { mutate: updatePassword } = useUpdateUserPassword({
		onSuccess: () => {
			toast.success('Your password has been set.');

			setHasSetPassword(true);
		},
		onError: (error) => {
			const defaultMessage = 'Something went wrong while setting password';
			const responseMessage = error?.response?.data?.message;
			const errorMessage = responseMessage && responseMessage.length <= 100
				? responseMessage
				: defaultMessage;

			toast.error(errorMessage);
		}
	});

	const {
		mutate: updateCampaignInvitation,
		isPending: isUpdatingUserCampaignInvite
	} = useUpdateUserCampaignInvitation({
		onSuccess: () => {
			setStep(1);
		},
		onError: (error) => {
			if (error?.response?.data?.already_invited) {
				setStep(1);

				return;
			}

			const defaultMessage = 'Something went wrong while setting password';
			const responseMessage = error?.response?.data?.message;
			const errorMessage = responseMessage && responseMessage.length <= 100
				? responseMessage
				: defaultMessage;

			toast.error(errorMessage);
		},
	});

	const {
		mutate: sendGuestInvite,
	} = useSendGuestInvite({
		onSuccess: () => {
			toast.success('Invite sent successfully');

			navigate({
				pathname: '/invitation/success',
				search: `?${searchParams.toString()}`
			});
		},
		onError: (error) => {
			const defaultMessage = 'Something went wrong while setting password';
			const responseMessage = error?.response?.data?.message;
			const errorMessage = responseMessage && responseMessage.length <= 100
				? responseMessage
				: defaultMessage;

			toast.error(errorMessage);
		},
	});

	const handleEmailSubmit = async () => {
		setError((prev) => ({
			...prev,
			email: ''
		}));

		try {
			if (!emailRegex.test(email)) {
				setError((prev) => ({
					...prev,
					email: 'Please enter a valid email'
				}));

				return;
			}
			const { data, error } = await fetchUserByEmail();

			if (error) {
				toast.error('Something went wrong!');

				return;
			}

			if (data) {
				if (!data.email_exists) {
					setIsAlreadyRegistered(false);

					updateContactInfo({
						flow: 'user-info-update',
						userID: userCurrentID,
						email
					});
				} else {
					const newUserID = data?.user?.id;

					if (newUserID) {
						localStorage.setItem('invitee_user_id', newUserID);

						setIsAlreadyRegistered(true);
						setHasSetPassword(data.has_password_set);

						updateCampaignInvitation({
							restaurantID,
							campaignID,
							campaignInviteID,
							userID: newUserID
						});
					}
				}
			}
		} catch (e) {
			console.log('error fetching user: ', e);
		}
	};

	const handlePasswordSubmit = async () => {
		if (!hasSetPassword) {
			if (!passwordRegex.test(newPassword)) {
				setError((prev) => ({
					...prev,
					newPassword: 'Password should contain atleast 6 characters'
				}));

				return;
			}

			if (newPassword !== confirmNewPassword) {
				setError((prev) => ({
					...prev,
					newPassword: 'Password doesn\'t match'
				}));

				return;
			}

			if (newPassword === confirmNewPassword) {
				updatePassword({
					userID: localStorage.getItem('invitee_user_id'),
					password: newPassword
				});
			}
		} else {
			if (!password) return;

			try {
				const data = await queryClient.fetchQuery({
					queryKey: ['userLogin', email, password],
					queryFn: () => userLogin({ email, pw: password })
				});

				if (data.user_exists) {
					if (guestInviteInfo) {
						setStep(2);
					} else {
						navigate({
							pathname: '/invitation/success',
							search: `?${searchParams.toString()}`
						});
					}
				}
			} catch (error) {
				if (error) {
					const defaultMessage = 'Something went wrong while signing in';
					const responseMessage = error?.response?.data?.message;
					const errorMessage = responseMessage && responseMessage.length <= 100
						? responseMessage
						: defaultMessage;

					toast.error(errorMessage);
				}
			}
		}
	};

	const handleGuestEmailSubmit = async () => {
		try {
			if (!emailRegex.test(guestEmail)) {
				setError((prev) => ({
					...prev,
					guestEmail: 'Please enter a valid email'
				}));

				return;
			}

			if (!guestInviteInfo && !guestInviteInfo.invitee) {
				toast.error('Something went wrong while finding guest info');

				return;
			}

			const { data, error } = await fetchGuestByEmail();

			if (error) {
				toast.error('Something went wrong while sending invite');

				return;
			}

			if (data) {
				if (!data.email_exists) {
					setIsAlreadyRegistered(false);

					updateContactInfo({
						flow: 'guest-info-update',
						userID: guestInviteInfo?.invitee?.id,
						email: guestEmail
					});
				} else {
					sendGuestInvite({
						restaurantID,
						campaignID,
						campaignInviteID: guestInviteInfo.id,
						email: guestEmail
					});
				}
			}
		} catch (e) {
			console.log('Error sending guest invite');
		}
	};

	return (
		<>
			{step === 0 && (
				<div className='d-flex flex-column gap-4'>
					<h3>What is the email address that we can save your reservation against?</h3>
					<input
						value={email}
						onChange={(e) => {
							setError((prev) => ({
								...prev,
								email: ''
							}));

							setEmail(e.target.value);
						}}
						className={Styles.input}
						placeholder='Email'
					/>
					<div className={Styles.errorText}>
						<p>{error.email || ''}</p>
					</div>
					<button
						disabled={
							!email
							|| isCheckingEmail
							|| isUpdatingUserContactInfo
							|| isUpdatingUserCampaignInvite
						}
						onClick={handleEmailSubmit}
						className={`${Styles.continue} mt-5`}>
						Continue
					</button>
				</div>
			)}
			{step === 1 && (
				<div className='d-flex flex-column gap-4' >
					<div className='d-flex flex-column gap-4 mb-4'>
						{!isAlreadyRegistered
							? <h3>{!hasSetPassword ? 'Set a password to continue' : 'Enter your password'}</h3>
							: <h3>It looks like you already have an account with us. Please sign in</h3>
						}
						{!isAlreadyRegistered && !hasSetPassword
							? <div className='d-flex flex-column gap-4'>
								<input
									value={newPassword}
									onChange={(e) => {
										setError((prev) => ({
											...prev,
											password: ''
										}));
										setNewPassword(e.target.value);
									}}
									type='password'
									className={Styles.input}
									placeholder='Password'
								/>
								<div>
									<input
										value={confirmNewPassword}
										onChange={(e) => setConfirmNewPassword(e.target.value)}
										type='password'
										className={Styles.input}
										placeholder='Confirm Password'
									/>
									<div className={Styles.errorText}>
										<p>{error.newPassword || ''}</p>
									</div>
								</div>
							</div>
							: <input
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								type='password'
								className={Styles.input}
								placeholder='Password'
							/>
						}
					</div>
					<button
						disabled={
							hasSetPassword
								? !password
								: !newPassword || !confirmNewPassword
						}
						onClick={handlePasswordSubmit}
						className={Styles.continue}>
						Continue
					</button>
				</div >
			)}
			{guestInviteInfo && step === 2 && (
				<div className='d-flex flex-column gap-4'>
					<h3>
						Let's make sure {guestInviteInfo?.invitee?.first_name} gets their invite as well.
					</h3>
					<div>
						<input
							value={guestEmail}
							onChange={(e) => {
								setError((prev) => ({
									...prev,
									guestEmail: ''
								}));

								setGuestEmail(e.target.value);
							}}
							className={Styles.input}
							placeholder='Email'
						/>
						<div className={Styles.errorText}>
							<p>{error.guestEmail || ''}</p>
						</div>
					</div>
					<button
						disabled={!guestEmail}
						onClick={handleGuestEmailSubmit}
						className={`${Styles.continue} mt-5`}>
						Continue
					</button>
				</div>
			)}
		</>
	);
};
